import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import PropTypes from "prop-types";
import clsx from "clsx";

import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";

import { Autocomplete } from "@material-ui/lab";

import { useAlert } from "react-alert";
import { useParams } from "react-router-dom";

import {
  getFindById,
  UpdatePrivateSale,
} from "../../../adminModel/privateSale";

import {
  getGroup,
  findArtist,
} from "../../../adminModel/group";

import Image from "./Image";

// =====================================================
// STYLES
// =====================================================

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },

  card: {
    width: "100%",
    border: "1px solid #e5e5e5",
    borderRadius: 8,
    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },

  cardHeader: {
    padding: theme.spacing(2.5, 3),
    backgroundColor: "#fafafa",
  },

  cardTitle: {
    fontSize: 17,
    fontWeight: 600,
    color: "#222",
  },

  cardSubtitle: {
    marginTop: 4,
    fontSize: 12,
    color: "#888",
  },

  cardContent: {
    padding: theme.spacing(3),
  },

  section: {
    width: "100%",
    marginBottom: theme.spacing(3),
  },

  sectionHeader: {
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: "1px solid #eeeeee",
  },

  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#333",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },

  sectionDescription: {
    marginTop: 3,
    fontSize: 11,
    color: "#999",
  },

  field: {
    marginTop: theme.spacing(1.5),

    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      borderRadius: 6,
    },

    "& .MuiInputBase-input": {
      fontSize: 13,
    },

    "& .MuiInputLabel-root": {
      fontSize: 13,
    },
  },

  multilineField: {
    marginTop: theme.spacing(1.5),

    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      borderRadius: 6,
      alignItems: "flex-start",
    },

    "& .MuiInputBase-input": {
      fontSize: 13,
      lineHeight: 1.6,
    },

    "& .MuiInputLabel-root": {
      fontSize: 13,
    },
  },

  artistField: {
    marginTop: theme.spacing(1.5),

    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      borderRadius: 6,
    },

    "& .MuiInputBase-input": {
      fontSize: 13,
    },

    "& .MuiInputLabel-root": {
      fontSize: 13,
    },
  },

  imageSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    border: "1px solid #e5e5e5",
    borderRadius: 8,
    backgroundColor: "#fafafa",
  },

  imageTitle: {
    marginBottom: theme.spacing(1.5),
    fontSize: 13,
    fontWeight: 600,
    color: "#333",
  },

  formControl: {
    width: "100%",
    marginTop: theme.spacing(1.5),

    "& .MuiOutlinedInput-root": {
      borderRadius: 6,
      backgroundColor: "#fff",
    },

    "& .MuiSelect-select": {
      fontSize: 13,
    },
  },

  actionWrapper: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: theme.spacing(1.5, 3),
    backgroundColor: "#fafafa",
  },

  updateButton: {
    minWidth: 145,
    height: 40,
    borderRadius: 6,
    textTransform: "none",
    fontSize: 13,
    fontWeight: 600,
    boxShadow: "none",

    "&:hover": {
      boxShadow: "none",
    },
  },

  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },

  mobileDivider: {
    display: "none",

    [theme.breakpoints.down("sm")]: {
      display: "block",
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },
}));

// =====================================================
// INITIAL FORM VALUES
// =====================================================

const initialValues = {
  privateSalesGroup: "",
  artist: "",
  signed_dated: "",
  medium: "",
  size: "",
  title: "",
  property: "",
  coa: "",
  provenance: "",
  exhibited: "",
  literature: "",
  write_up: "",
  lot_group: "",
  lot_details_html: "",
  alias: "",
  isVisible: 1,
};

// =====================================================
// COMPONENT
// =====================================================

const EditPrivateSale = ({
  className,
  ...rest
}) => {
  const classes = useStyles();
  const alert = useAlert();

  const { id } = useParams();

  // ===================================================
  // STATE
  // ===================================================

  const [loading, setLoading] = useState(false);

  const [getImage, setImage] = useState([]);

  const [group, setGroup] = useState([]);

  const [artistOptions, setArtistOptions] =
    useState([]);

  const [artistLoading, setArtistLoading] =
    useState(false);

  const [values, setValues] =
    useState(initialValues);

  // ===================================================
  // FETCH INITIAL DATA
  // ===================================================

  const fetchInitialData = useCallback(
    async () => {
      setLoading(true);

      try {
        // Get groups
        const groups = await getGroup();

        setGroup(groups || []);

        // Get private sale details
        const response =
          await getFindById(id);

        setImage(response || []);

        // Populate form
        if (
          response &&
          response.data &&
          response.data.length > 0
        ) {
          const data =
            response.data[0];

          setValues({
            privateSalesGroup:
              data.privateSalesGroup ||
              "",

            artist:
              data.artist || "",

            signed_dated:
              data.signed_dated || "",

            medium:
              data.medium || "",

            size:
              data.size || "",

            title:
              data.title || "",

            property:
              data.property || "",

            coa:
              data.coa || "",

            provenance:
              data.provenance || "",

            exhibited:
              data.exhibited || "",

            literature:
              data.literature || "",

            write_up:
              data.write_up || "",

            lot_group:
              data.lot_group || "",

            lot_details_html:
              data.lot_details_html || "",

            alias:
              data.alias || "",

            isVisible:
              data.isVisible !==
              undefined
                ? data.isVisible
                : 1,
          });
        }
      } catch (error) {
        console.error(
          "Failed to load private sale:",
          error
        );

        alert.error(
          "Failed to load data"
        );
      } finally {
        setLoading(false);
      }
    },
    [id, alert]
  );

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // ===================================================
  // FORM CHANGE
  // ===================================================

  const handleChange = (event) => {
    const {
      name,
      value,
    } = event.target;

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ===================================================
  // ARTIST SEARCH
  // ===================================================

  const handleArtistSearch = async (
    event,
    newInputValue
  ) => {
    const keyword =
      newInputValue || "";

    setValues((prev) => ({
      ...prev,
      artist: keyword,
    }));

    // Don't search for less than 2 characters
    if (keyword.length < 2) {
      setArtistOptions([]);
      return;
    }

    setArtistLoading(true);

    try {
      const response =
        await findArtist(keyword);

      const data =
        response &&
        response.data
          ? response.data
          : response;

      if (Array.isArray(data)) {
        const formatted =
          data
            .map(
              (artist) =>
                `${artist.firstname || ""} ${
                  artist.lastname || ""
                }`.trim()
            )
            .filter(Boolean);

        setArtistOptions(formatted);
      } else {
        setArtistOptions([]);
      }
    } catch (error) {
      console.error(
        "Artist search error:",
        error
      );

      setArtistOptions([]);
    } finally {
      setArtistLoading(false);
    }
  };

  // ===================================================
  // SUBMIT
  // ===================================================

  const handleFormSubmit = async (
    event
  ) => {
    event.preventDefault();

    setLoading(true);

    try {
      const result =
        await UpdatePrivateSale(
          id,
          values
        );

      alert.success(
        result.msg ||
          "Updated successfully"
      );
    } catch (error) {
      console.error(
        "Update error:",
        error
      );

      alert.error(
        "Server Error"
      );
    } finally {
      setLoading(false);
    }
  };

  // ===================================================
  // RENDER
  // ===================================================

  return (
    <div
      className={clsx(
        classes.root,
        className
      )}
      {...rest}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <form
            onSubmit={handleFormSubmit}
          >
            <Card
              className={classes.card}
            >
              {/* =================================
                  HEADER
              ================================= */}

              <CardHeader
                className={
                  classes.cardHeader
                }
                title={
                  <Typography
                    className={
                      classes.cardTitle
                    }
                  >
                    Edit Private Sale
                  </Typography>
                }
                subheader={
                  <Typography
                    className={
                      classes.cardSubtitle
                    }
                  >
                    Update artwork information
                    and publication settings
                  </Typography>
                }
              />

              <Divider />

              {/* =================================
                  CONTENT
              ================================= */}

              <CardContent
                className={
                  classes.cardContent
                }
              >
                <Grid
                  container
                  spacing={4}
                >
                  {/* =================================
                      LEFT COLUMN
                  ================================= */}

                  <Grid
                    item
                    md={6}
                    xs={12}
                  >
                    <div
                      className={
                        classes.section
                      }
                    >
                      <div
                        className={
                          classes.sectionHeader
                        }
                      >
                        <Typography
                          className={
                            classes.sectionTitle
                          }
                        >
                          Artwork Information
                        </Typography>

                        <Typography
                          className={
                            classes.sectionDescription
                          }
                        >
                          Basic artwork details
                        </Typography>
                      </div>

                      {/* GROUP */}

                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        select
                        label="Group"
                        name="privateSalesGroup"
                        variant="outlined"
                        value={
                          values.privateSalesGroup
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      >
                        {group.map(
                          (item) => (
                            <MenuItem
                              key={
                                item.id
                              }
                              value={
                                item.groupname
                              }
                            >
                              {
                                item.groupname
                              }
                            </MenuItem>
                          )
                        )}
                      </TextField>

                      {/* ARTIST */}

                      <Autocomplete
                        className={
                          classes.artistField
                        }
                        freeSolo
                        options={
                          artistOptions
                        }
                        loading={
                          artistLoading
                        }
                        value={
                          values.artist
                        }
                        onInputChange={
                          handleArtistSearch
                        }
                        onChange={(
                          event,
                          newValue
                        ) => {
                          setValues(
                            (prev) => ({
                              ...prev,
                              artist:
                                newValue ||
                                "",
                            })
                          );
                        }}
                        renderInput={(
                          params
                        ) => (
                          <TextField
                            {...params}
                            fullWidth
                            label="Artist"
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {artistLoading && (
                                    <CircularProgress
                                      color="inherit"
                                      size={18}
                                    />
                                  )}

                                  {
                                    params
                                      .InputProps
                                      .endAdornment
                                  }
                                </>
                              ),
                            }}
                          />
                        )}
                      />

                      {/* TITLE */}

                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        label="Title"
                        name="title"
                        variant="outlined"
                        value={
                          values.title
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />

                      {/* MEDIUM */}

                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        label="Medium"
                        name="medium"
                        variant="outlined"
                        value={
                          values.medium
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />

                      {/* SIGNED / DATED */}

                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        label="Signed / Dated"
                        name="signed_dated"
                        variant="outlined"
                        value={
                          values.signed_dated
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />

                      {/* LOT GROUP */}

                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        label="Lot Group"
                        name="lot_group"
                        variant="outlined"
                        value={
                          values.lot_group
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />

                      {/* SIZE */}

                      <TextField
                        className={
                          classes.multilineField
                        }
                        fullWidth
                        multiline
                        rows={2}
                        label="Size"
                        name="size"
                        variant="outlined"
                        value={
                          values.size
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />

                      {/* IMAGE */}

                      <Box
                        className={
                          classes.imageSection
                        }
                      >
                        <Typography
                          className={
                            classes.imageTitle
                          }
                        >
                          Artwork Image
                        </Typography>

                        <Image
                          ItemData={
                            getImage
                          }
                        />
                      </Box>
                    </div>
                  </Grid>

                  {/* =================================
                      RIGHT COLUMN
                  ================================= */}

                  <Grid
                    item
                    md={6}
                    xs={12}
                  >
                    <div
                      className={
                        classes.section
                      }
                    >
                      <div
                        className={
                          classes.sectionHeader
                        }
                      >
                        <Typography
                          className={
                            classes.sectionTitle
                          }
                        >
                          Additional Details
                        </Typography>

                        <Typography
                          className={
                            classes.sectionDescription
                          }
                        >
                          Supporting information
                          and artwork documentation
                        </Typography>
                      </div>

                      {/* PROPERTY */}

                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        label="Property"
                        name="property"
                        variant="outlined"
                        value={
                          values.property
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />

                      {/* COA */}

                      <TextField
                        className={
                          classes.multilineField
                        }
                        fullWidth
                        multiline
                        minRows={3}
                        label="COA"
                        name="coa"
                        variant="outlined"
                        value={
                          values.coa
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />

                      {/* EXHIBITED */}

                      <TextField
                        className={
                          classes.multilineField
                        }
                        fullWidth
                        multiline
                        minRows={3}
                        label="Exhibited"
                        name="exhibited"
                        variant="outlined"
                        value={
                          values.exhibited
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />

                      {/* PROVENANCE */}

                      <TextField
                        className={
                          classes.multilineField
                        }
                        fullWidth
                        multiline
                        minRows={3}
                        label="Provenance"
                        name="provenance"
                        variant="outlined"
                        value={
                          values.provenance
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />

                      {/* LITERATURE */}

                      <TextField
                        className={
                          classes.multilineField
                        }
                        fullWidth
                        multiline
                        minRows={3}
                        label="Literature"
                        name="literature"
                        variant="outlined"
                        value={
                          values.literature
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />

                      {/* WRITE UP */}

                      <TextField
                        className={
                          classes.multilineField
                        }
                        fullWidth
                        multiline
                        minRows={3}
                        label="Write Up"
                        name="write_up"
                        variant="outlined"
                        value={
                          values.write_up
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />

                      {/* ALIAS */}

                      <TextField
                        className={
                          classes.multilineField
                        }
                        fullWidth
                        multiline
                        minRows={3}
                        label="Alias"
                        name="alias"
                        variant="outlined"
                        value={
                          values.alias
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />

                      {/* VISIBILITY */}

                      <FormControl
                        variant="outlined"
                        className={
                          classes.formControl
                        }
                      >
                        <InputLabel
                          shrink
                        >
                          Visibility Status
                        </InputLabel>

                        <Select
                          name="isVisible"
                          value={
                            values.isVisible
                          }
                          onChange={
                            handleChange
                          }
                          label="Visibility Status"
                        >
                          <MenuItem value={1}>
                            Show
                          </MenuItem>

                          <MenuItem value={2}>
                            Unshow
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                  </Grid>
                </Grid>
              </CardContent>

              {/* =================================
                  FOOTER
              ================================= */}

              <Divider />

              <Box
                className={
                  classes.actionWrapper
                }
              >
                <Button
                  className={
                    classes.updateButton
                  }
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={loading}
                >
                  {loading
                    ? "Updating..."
                    : "Update Details"}
                </Button>
              </Box>
            </Card>
          </form>
        </Grid>
      </Grid>

      {/* =================================
          LOADING
      ================================= */}

      <Backdrop
        className={
          classes.backdrop
        }
        open={loading}
      >
        <CircularProgress
          color="inherit"
        />
      </Backdrop>
    </div>
  );
};

EditPrivateSale.propTypes = {
  className: PropTypes.string,
};

export default EditPrivateSale;